import { z } from "zod";

// Comprehensive validation schemas for security

export const emailSchema = z
  .string()
  .trim()
  .email({ message: "Invalid email address" })
  .max(255, { message: "Email must be less than 255 characters" })
  .toLowerCase();

export const phoneSchema = z
  .string()
  .trim()
  .regex(/^[\d\s\-\(\)\+]+$/, { message: "Invalid phone number format" })
  .min(10, { message: "Phone number must be at least 10 digits" })
  .max(20, { message: "Phone number must be less than 20 characters" })
  .optional()
  .or(z.literal(""));

export const nameSchema = z
  .string()
  .trim()
  .min(2, { message: "Name must be at least 2 characters" })
  .max(100, { message: "Name must be less than 100 characters" })
  .regex(/^[a-zA-Z\s\-'\.]+$/, { 
    message: "Name can only contain letters, spaces, hyphens, apostrophes, and periods" 
  });

export const addressSchema = z
  .string()
  .trim()
  .max(500, { message: "Address must be less than 500 characters" })
  .optional()
  .or(z.literal(""));

export const notesSchema = z
  .string()
  .trim()
  .max(2000, { message: "Notes must be less than 2000 characters" })
  .optional()
  .or(z.literal(""));

export const bookingSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: phoneSchema,
  service_type: z.string().trim().min(1, { message: "Service type is required" }),
  property_address: addressSchema,
  scheduled_date: z.date({ required_error: "Please select a date" }),
  scheduled_time: z
    .string()
    .regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, { message: "Invalid time format" }),
  notes: notesSchema,
});

export const leadSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: phoneSchema,
  brokerage: z
    .string()
    .trim()
    .max(200, { message: "Brokerage name must be less than 200 characters" })
    .optional()
    .or(z.literal("")),
  address: addressSchema,
  notes: notesSchema,
  source: z.string().trim().max(100, { message: "Source must be less than 100 characters" }),
});

export const taskSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, { message: "Title is required" })
    .max(200, { message: "Title must be less than 200 characters" }),
  description: z
    .string()
    .trim()
    .max(2000, { message: "Description must be less than 2000 characters" })
    .optional()
    .or(z.literal("")),
  due_date: z.date().optional(),
});

export const noteSchema = z.object({
  content: z
    .string()
    .trim()
    .min(1, { message: "Content is required" })
    .max(5000, { message: "Content must be less than 5000 characters" }),
});

// Password validation with security requirements
export const passwordSchema = z
  .string()
  .min(8, { message: "Password must be at least 8 characters" })
  .max(128, { message: "Password must be less than 128 characters" })
  .regex(/[A-Z]/, { message: "Password must contain at least one uppercase letter" })
  .regex(/[a-z]/, { message: "Password must contain at least one lowercase letter" })
  .regex(/[0-9]/, { message: "Password must contain at least one number" })
  .regex(/[^A-Za-z0-9]/, { message: "Password must contain at least one special character" });

export const signUpSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Sanitize HTML to prevent XSS (escapes all tags)
export function sanitizeHtml(html: string): string {
  const div = document.createElement('div');
  div.textContent = html;
  return div.innerHTML;
}

// Sanitize a limited subset of rich-text HTML for safe rendering.
// Allows basic formatting tags used by our editors.
export function sanitizeRichTextHtml(html: string): string {
  try {
    // Some editors/clipboard flows can store HTML as escaped text (e.g. "&lt;div&gt;") or even double-escaped.
    // Decode entities first so allowed tags can be properly sanitized and rendered.
    const decodeEntities = (value: string) => {
      let current = value;
      for (let i = 0; i < 3; i++) {
        // Only bother if it looks encoded.
        if (!/&(?:lt|gt|amp|quot|#39|nbsp);/i.test(current)) break;
        const textarea = document.createElement("textarea");
        textarea.innerHTML = current;
        const next = textarea.value.replace(/\u00A0/g, " ");
        if (next === current) break;
        current = next;
      }
      return current;
    };

    const decoded = decodeEntities(html);

    // Fast-path for plain text
    if (!/[<>]/.test(decoded)) {
      return sanitizeHtml(decoded).replace(/\n/g, "<br>");
    }

    const allowedTags = new Set([
      'b', 'strong', 'i', 'em', 'u', 's', 'strike',
      'br',
      'p', 'div', 'span',
      'ul', 'ol', 'li',
      'blockquote',
      'code', 'pre',
      'a',
      'img',
    ]);

    const parser = new DOMParser();
    const doc = parser.parseFromString(decoded, 'text/html');

    const walk = (node: Node) => {
      // Remove comments
      if (node.nodeType === Node.COMMENT_NODE) {
        node.parentNode?.removeChild(node);
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();

        if (!allowedTags.has(tag)) {
          // Replace unknown tags with their text content
          const text = doc.createTextNode(el.textContent || '');
          el.parentNode?.replaceChild(text, el);
          return;
        }

        // Strip attributes except allowlist
        const attrs = Array.from(el.attributes);
        for (const attr of attrs) {
          el.removeAttribute(attr.name);
        }

        if (tag === 'a') {
          const href = (el as HTMLAnchorElement).getAttribute('href') || '';
          const safeHref = href.trim();
          // Only allow http(s) and mailto
          if (!/^https?:\/\//i.test(safeHref) && !/^mailto:/i.test(safeHref)) {
            // Remove link but keep text
            const text = doc.createTextNode(el.textContent || '');
            el.parentNode?.replaceChild(text, el);
            return;
          }
          (el as HTMLAnchorElement).setAttribute('href', safeHref);
          (el as HTMLAnchorElement).setAttribute('target', '_blank');
          (el as HTMLAnchorElement).setAttribute('rel', 'noopener noreferrer');
        }

        if (tag === 'img') {
          const src = (el as HTMLImageElement).getAttribute('src') || '';
          const safeSrc = src.trim();
          // Only allow http(s) images
          if (!/^https?:\/\//i.test(safeSrc)) {
            el.parentNode?.removeChild(el);
            return;
          }
          (el as HTMLImageElement).setAttribute('src', safeSrc);
          // keep alt for accessibility
          (el as HTMLImageElement).setAttribute('alt', (el as HTMLImageElement).getAttribute('alt') || 'Embedded image');
          (el as HTMLImageElement).setAttribute('loading', 'lazy');
        }
      }

      // Walk children (copy because we may mutate)
      const children = Array.from(node.childNodes);
      for (const child of children) walk(child);
    };

    walk(doc.body);
    return doc.body.innerHTML;
  } catch {
    // Fallback: escape everything
    return sanitizeHtml(html);
  }
}

// Rate limiting helper for client-side
export class ClientRateLimiter {
  private attempts: Map<string, { count: number; resetAt: number }> = new Map();

  canAttempt(key: string, maxAttempts: number = 5, windowMs: number = 60000): boolean {
    const now = Date.now();
    const record = this.attempts.get(key);

    if (!record || now > record.resetAt) {
      this.attempts.set(key, { count: 1, resetAt: now + windowMs });
      return true;
    }

    if (record.count >= maxAttempts) {
      return false;
    }

    record.count++;
    return true;
  }

  getRemainingTime(key: string): number {
    const record = this.attempts.get(key);
    if (!record) return 0;
    return Math.max(0, record.resetAt - Date.now());
  }
}
